import { Breadcrumb, Button, Divider, Empty, Layout, Result, Skeleton } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiUnifiedTransactionDetail } from "../../../api/transactions";
import { formatDateTime, formatExpectedOnDate, formatPhoneWithCountry, formatPrice } from "../../../utils/format";
import {
  autoSplitStatusLabel,
  captureMethodLabel,
  cardBrandLabel,
  paymentTypeLabel,
  transactionStatusMeta,
} from "../../../utils/transactions";
import { isSuperUser, userHasPermission } from "../../../helpers/user";
import ReceivablesSection from "./ReceivablesSection/ReceivablesSection";
import "./SaleInfo.scss";
import SaleInfoField from "./SaleInfoField/SaleInfoField";
import SaleInfoSection from "./SaleInfoSection/SaleInfoSection";
import SplitModal from "./SplitModal/SplitModal";
import SplitRulesSection from "./SplitRulesSection/SplitRulesSection";
import VoidSaleButton from "./VoidSaleButton/VoidSaleButton";

const SaleInfo = () => {
  const { slug: transactionID } = useParams();
  const [dataTransaction, setDataTransaction] = useState();
  const [loading, setLoading] = useState(true);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const navigate = useNavigate();

  const handleList = useCallback(async () => {
    setLoading(true);
    setErrorStatus("");
    try {
      setDataTransaction(await apiUnifiedTransactionDetail(transactionID));
    } catch (error) {
      setErrorStatus(error?.status === 500 ? "500" : "404");
    } finally {
      setLoading(false);
    }
  }, [transactionID]);

  useEffect(() => {
    handleList();
  }, [handleList]);

  if (errorStatus) {
    return (
      <div className="SaleInfo">
        <Layout style={{ padding: "1.25rem" }}>
          <Result
            status={errorStatus}
            title={errorStatus}
            subTitle={
              errorStatus === "500"
                ? "Ocorreu um erro no servidor, tente novamente mais tarde."
                : "Ocorreu um erro, tente novamente mais tarde."
            }
            extra={
              <Button onClick={() => navigate("/listagem-vendas")} type="primary">
                Listagem de vendas
              </Button>
            }
          />
        </Layout>
      </div>
    );
  }

  const status = transactionStatusMeta(dataTransaction?.status);
  const headerAmount = formatPrice(dataTransaction?.original_amount ?? dataTransaction?.amount);

  return (
    <div className="SaleInfo">
      <Layout style={{ padding: "1.25rem" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          {loading ? <Skeleton active paragraph={{ rows: 0 }} /> : <h2 className="title-page">{headerAmount}</h2>}
          <Divider />
          {loading ? <Skeleton active paragraph={{ rows: 0 }} /> : (
            <>
              <Breadcrumb.Item>{headerAmount}</Breadcrumb.Item>
              <Breadcrumb.Item>VENDA #{dataTransaction?.id}</Breadcrumb.Item>
            </>
          )}
        </Breadcrumb>
        <SaleInfoSection
          margin={0}
          title="Informações da venda"
          extra={
            loading ? (
              <Skeleton.Button active size="small" style={{ width: 110 }} />
            ) : (
              <span className={`badge ${status.className}`}>{status.label}</span>
            )
          }
        >
          {loading ? <Skeleton active paragraph={{ rows: 3 }} /> : (
            <div className="container-inputs">
              <div className="container-left">
                <SaleInfoField label="Data/hora" value={formatDateTime(dataTransaction?.created_at)} />
                <SaleInfoField label="Lojista" value={dataTransaction?.statement_descriptor} />
                <SaleInfoField label="Valor bruto" value={formatPrice(dataTransaction?.amount)} />
                <SaleInfoField label="Terminal" value={dataTransaction?.pos_identification} />
                <SaleInfoField label="Identificador Adquirente" value={dataTransaction?.acquirer_charge_id} />
              </div>
              <div className="container-right">
                <SaleInfoField label="Tipo" value={paymentTypeLabel(dataTransaction?.payment_type)} />
                <SaleInfoField label="Parcelas" value={dataTransaction?.installments ?? 1} />
                <SaleInfoField
                  label="Método de pagamento"
                  value={captureMethodLabel(dataTransaction?.capture_method)}
                />
                <SaleInfoField
                  label="Previsão de pagamento"
                  value={
                    dataTransaction?.expected_on ? formatExpectedOnDate(dataTransaction.expected_on) : undefined
                  }
                />
              </div>
            </div>
          )}
        </SaleInfoSection>
        {!loading && (
          <>
            <SaleInfoSection
              title="Informações do comprador"
              minHeight={dataTransaction?.buyer ? 280 : 40}
            >
              {dataTransaction?.buyer ? (
                <div className="container-inputs">
                  <div className="container-left">
                    <SaleInfoField label="Nome" value={dataTransaction.buyer.name} />
                    <SaleInfoField label="Email" value={dataTransaction.buyer.email} />
                  </div>
                  <div className="container-right">
                    <SaleInfoField
                      label="Telefone"
                      value={formatPhoneWithCountry(
                        dataTransaction.buyer.country_code,
                        dataTransaction.buyer.phone_number
                      )}
                    />
                  </div>
                </div>
              ) : (
                <h3>Comprador não foi identificado</h3>
              )}
            </SaleInfoSection>

            {(dataTransaction?.payment_type === "credit" || dataTransaction?.payment_type === "debit") && (
              <SaleInfoSection title="Informações de pagamento">
                <div className="container-inputs">
                  <div className="container-left">
                    <SaleInfoField label="Bandeira" value={cardBrandLabel(dataTransaction?.card_brand)} />
                    <SaleInfoField label="Nome do titular" value={dataTransaction?.holder_name} />
                  </div>
                  <div className="container-right">
                    <SaleInfoField label="Número do cartão" value={dataTransaction?.masked_number} />
                  </div>
                </div>
              </SaleInfoSection>
            )}

            <SaleInfoSection title="Informações do autorizador">
              <div className="container-inputs">
                <div className="container-left">
                  <SaleInfoField label="Código de Autorização" value={dataTransaction?.authorization_code} />
                  <SaleInfoField label="NSU" value={dataTransaction?.authorization_nsu} />
                  <SaleInfoField label="ID do autorizador" value={dataTransaction?.acquirer_transaction_id} />
                </div>
                <div className="container-right">
                  <SaleInfoField label="Autorizador" value={dataTransaction?.gateway_authorizer} uppercase />
                  <SaleInfoField
                    label="Retorno do adquirente"
                    value={[
                      dataTransaction?.acquirer_response_code,
                      dataTransaction?.acquirer_response_message,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  />
                </div>
              </div>
            </SaleInfoSection>

            {userHasPermission("split_transaction") && (
              <>
                <ReceivablesSection sale={dataTransaction} />
                <SplitRulesSection
                  sale={dataTransaction}
                  onCreateSplit={() => setShowSplitModal(true)}
                  onSplitDeleted={handleList}
                />
              </>
            )}

            {isSuperUser() && (
              <SaleInfoSection title="Split automático" minHeight={150}>
                <div className="container-inputs">
                  <div className="container-left">
                    <SaleInfoField
                      label="Status"
                      value={autoSplitStatusLabel(dataTransaction?.auto_split?.status)}
                    />
                    <SaleInfoField
                      label="Taxa do adquirente"
                      value={formatPrice(dataTransaction?.acquirer_fee)}
                    />
                  </div>
                  <div className="container-right">
                    <SaleInfoField label="Tentativas" value={dataTransaction?.auto_split?.retries ?? 0} />
                    <SaleInfoField label="MDR" value={formatPrice(dataTransaction?.mdr)} />
                  </div>
                </div>
                <div className="container-textarea">
                  <SaleInfoField
                    label="Detalhes do processamento"
                    value={dataTransaction?.auto_split?.error}
                    multiline
                  />
                </div>
              </SaleInfoSection>
            )}

            <SaleInfoSection title="Documentos relacionados" minHeight={150}>
              <Empty description="Nenhum documento relacionado a esta venda." />
            </SaleInfoSection>

            <SaleInfoSection title="Local da compra" minHeight={150}>
              <p>Localização não informada pelo comprador ou dispositivo.</p>
            </SaleInfoSection>

            <VoidSaleButton sale={dataTransaction} />
          </>
        )}
      </Layout>

      <SplitModal
        visible={showSplitModal}
        onCancel={() => setShowSplitModal(false)}
        onSuccess={handleList}
        dataTransaction={dataTransaction}
        loading={loading}
      />
    </div>
  );
};

export default SaleInfo;
